import axios from "axios";
import { ZOHO_SCOPES } from "../config/scope";
import {
  ADMIN_API_URL,
  DATA_CENTER_API_NAME,
  DATA_CENTER_URL_API_NAME,
  EXTENSION_IDENTIFIER,
  IS_SANDBOX,
  ORG_ID_API_NAME,
  SERVICE_API_URL,
  ZOHO_CONN,
} from "../config/serviceConstant";
import { accountUrlMap, dataCenterMap } from "../config/zohoConstant";

// ── Org Variable helpers ──

export const setOrgId = async (ZOHO) => {
  try {
    const orgInfo = await ZOHO.CRM.CONFIG.getOrgInfo();
    const orgId = orgInfo?.org?.[0]?.id || "";
    const orgData = { apiname: ORG_ID_API_NAME, value: orgId };
    await ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", orgData);
    return orgId;
  } catch (error) {
    return null;
  }
};

export const setUpOrgVariable = async (ZOHO, variableKey, variableValue) => {
  try {
    const orgData = { apiname: variableKey, value: variableValue };
    await ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", orgData);
    return true;
  } catch (error) {
    return false;
  }
};

export const setUpDataCenterUrl = async (ZOHO) => {
  try {
    const currentEnv = await ZOHO.CRM.CONFIG.GetCurrentEnvironment();
    let dataCenterUrl = dataCenterMap?.[currentEnv?.deployment] || "";
    if (IS_SANDBOX === "true") {
      dataCenterUrl = `https://plugin-${EXTENSION_IDENTIFIER}.zohosandbox.com`;
    }
    const dataCenterData = {
      apiname: DATA_CENTER_URL_API_NAME,
      value: dataCenterUrl,
    };
    await ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", dataCenterData);
    return dataCenterUrl;
  } catch (error) {
    return null;
  }
};

export const setUpDataCenter = async (ZOHO) => {
  try {
    const currentEnv = await ZOHO.CRM.CONFIG.GetCurrentEnvironment();
    const dataCenterData = {
      apiname: DATA_CENTER_API_NAME,
      value: currentEnv?.deployment,
    };
    await ZOHO.CRM.CONNECTOR.invokeAPI("crm.set", dataCenterData);
    return currentEnv?.deployment;
  } catch (error) {
    return null;
  }
};

export const getOrgVariable = async (ZOHO, variableKey) => {
  try {
    const variableValue = await ZOHO.CRM.API.getOrgVariable(variableKey);
    return variableValue?.Success?.Content || null;
  } catch (error) {
    return null;
  }
};

// ── Ext widget registration ──

// Register a CRM extension widget (related list, custom action, etc.) with Zoho
// through the admin server. Idempotent on the backend, so it's safe to call
// whenever CRM auth is confirmed. `headers` is the standard zohoHeaders blob
// (orgid / apikey / connname / datacenterurl / datacenter); `widget` carries the
// display payload. apiDomain defaults to the request's datacenterurl.
export const registerExtWidget = async (headers, widget = {}) => {
  try {
    const config = {
      method: "POST",
      url: ADMIN_API_URL + "/data/zoho/crm/extwidgets",
      headers,
      data: {
        apiDomain: widget.apiDomain || headers?.datacenterurl,
        widgetName: widget.name,
        widgetDescription: widget.description,
        widgetUrl: widget.url,
        widgetType: widget.type,
      },
    };
    const resp = await axios(config);
    return resp?.data;
  } catch (error) {
    console.error("registerExtWidget error:", error);
    return null;
  }
};

// ── Zoho CRM Auth ──

const getAccountUrl = (countryCode) => {
  return accountUrlMap[countryCode];
};

export const getZohoAuthUrl = async (zohoBody) => {
  try {
    const config = {
      method: "POST",
      url: ADMIN_API_URL + "/auth/zoho/authenticate",
      data: {
        orgId: zohoBody?.orgid,
        apiKey: zohoBody?.apikey,
        zapiKey: zohoBody?.zapikey,
        accountsUrl: getAccountUrl(zohoBody?.datacenter),
        connName: ZOHO_CONN,
        scope: ZOHO_SCOPES,
        redirectUrl: ADMIN_API_URL + "/auth/zoho/callback",
        crmApiDomain: zohoBody?.datacenterurl,
      },
    };

    const authUrlResp = await axios(config);
    return authUrlResp?.data?.authUrl;
  } catch (error) {
    return null;
  }
};

export const checkZohoAuth = async (zohoHeaders) => {
  try {
    const config = {
      method: "GET",
      url: ADMIN_API_URL + "/auth/zoho/verifyauth",
      headers: zohoHeaders,
    };
    const authCheck = await axios(config);
    if (authCheck?.data?.org?.length > 0) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const verifyZohoAuth = async (zohoHeaders) => {
  try {
    const config = {
      method: "GET",
      url: ADMIN_API_URL + "/auth/zoho/verifyauth",
      headers: zohoHeaders,
    };
    const authCheck = await axios(config);
    if (authCheck?.data?.org?.length > 0) {
      return { isAuthenticated: true };
    }
    return { isAuthenticated: false };
  } catch (error) {
    return { isAuthenticated: false };
  }
};

export const revokeZoho = async (zohoHeaders) => {
  try {
    const config = {
      method: "GET",
      url: ADMIN_API_URL + "/auth/zoho/revoke",
      headers: zohoHeaders,
    };
    const revokeCheck = await axios(config);
    if (revokeCheck?.data || !revokeCheck?.error) return true;
    return false;
  } catch (error) {
    return false;
  }
};

// ── Zoho → Shopify Workflows (two-way sync toggle) ──

// Enable/disable the three default Zoho → Shopify outbound workflows that
// drive the "push CRM changes back to Shopify" direction. Lives on the
// service server (SERVICE_API_URL), not the admin server.
//
//   active === true  -> body { active: true }  (re-enable all 3 workflows)
//   active === false -> empty body {}          (disable all 3 workflows)
//
// Headers: orgid, apikey, datacenterurl (the bundle the backend expects).
export const setZohoWorkflowsStatus = async (headers, active) => {
  try {
    const resp = await axios({
      method: "PUT",
      url: `${SERVICE_API_URL}/zoho/workflows/status`,
      headers: { ...headers, "Content-Type": "application/json" },
      data: active ? { active: true } : {},
    });
    return resp?.data ?? true;
  } catch (error) {
    console.error("setZohoWorkflowsStatus error:", error);
    return null;
  }
};

// ── User Data ──

export const getCurrentUser = async (ZOHO) => {
  try {
    const resp = await ZOHO.CRM.CONFIG.getCurrentUser();
    return resp?.users?.[0] || null;
  } catch (error) {
    return null;
  }
};

export const getAllUsers = async (ZOHO) => {
  try {
    const resp = await ZOHO.CRM.API.getAllUsers({ Type: "AllUsers" });
    return resp?.users || [];
  } catch (error) {
    return [];
  }
};

// ── Zoho CRM Modules & Fields ──

export const getZohoModules = async (ZOHO) => {
  try {
    const resp = await ZOHO.CRM.META.getModules();
    const allModules = resp?.modules || [];
    return allModules.filter((m) => m.isBlueprintSupported);
  } catch (error) {
    console.error("getZohoModules error:", error);
    return [];
  }
};

export const getZohoModuleFields = async (ZOHO, moduleName) => {
  try {
    const resp = await ZOHO.CRM.META.getFields({ Entity: moduleName });
    const allFields = resp?.fields || [];
    return allFields.filter((f) => f.field_read_only !== true);
  } catch (error) {
    console.error("getZohoModuleFields error:", error);
    return [];
  }
};

// REST variant: fetches Zoho CRM module fields via the ecommerce service
// instead of `ZOHO.CRM.META.getFields`. Used by FieldMapping so subform
// sub-fields (e.g. Product_Details on inventory modules) come through
// fully populated, which the embedded SDK call does not return.
//
// Endpoint: `${SERVICE_API_URL}/getcrmfields/{moduleName}`
//   e.g. http://localhost:3021/ecommerce/easyshopifyforzohocrm/v3/getcrmfields/Sales_Orders
//
// Headers: standard auth bundle (orgid, connname, apikey, datacenterurl,
// datacenter, shopifyshopurl). Pass the same `headers` object the rest of
// the components use for service calls.
// Normalise a Zoho field object returned by the REST endpoint (camelCase
// keys: apiName, displayName, type, lookupModuleApiName, required,
// subformModuleApiName, subfields, …) into the SDK shape (snake_case keys:
// api_name, display_label, data_type, lookup.module.api_name,
// system_mandatory, subform.fields, …) that the rest of the codebase
// already expects. Both shapes survive (the original keys are spread
// first), but the snake_case ones are the canonical reference any
// consumer should read from.
//
// Recurses into `subfields` so subform sub-fields (e.g. Ordered_Items'
// Product_Name, Parent_Id, …) are normalised the same way and surfaced
// under `subform.fields` for `getSubformContext`.
const normaliseRestZohoField = (raw) => {
  if (!raw || typeof raw !== "object") return raw;
  const apiName = raw.apiName ?? raw.api_name ?? "";
  const displayLabel =
    raw.displayName ?? raw.display_label ?? raw.field_label ?? apiName ?? "";
  const dataType = raw.type ?? raw.data_type ?? "";
  const lookupModuleApiName =
    raw.lookupModuleApiName ??
    raw.lookup?.module?.api_name ??
    (typeof raw.lookup?.module === "string" ? raw.lookup.module : "") ??
    "";
  const subformModuleApiName =
    raw.subformModuleApiName ??
    raw.subform?.module?.api_name ??
    (typeof raw.subform?.module === "string" ? raw.subform.module : "") ??
    "";
  const subfields = Array.isArray(raw.subfields)
    ? raw.subfields.map(normaliseRestZohoField)
    : Array.isArray(raw.subform?.fields)
    ? raw.subform.fields.map(normaliseRestZohoField)
    : Array.isArray(raw.fields)
    ? raw.fields.map(normaliseRestZohoField)
    : [];
  return {
    ...raw,
    api_name: apiName,
    display_label: displayLabel,
    field_label: raw.field_label ?? displayLabel,
    data_type: dataType,
    lookup: lookupModuleApiName
      ? { module: { api_name: lookupModuleApiName } }
      : raw.lookup,
    subform:
      subformModuleApiName || subfields.length
        ? {
            module: subformModuleApiName
              ? { api_name: subformModuleApiName }
              : raw.subform?.module,
            fields: subfields,
          }
        : raw.subform,
    subfields,
    // `required` is what the REST endpoint sends; SDK calls it
    // `system_mandatory`. Map both so downstream auto-populate works.
    system_mandatory:
      raw.required ?? raw.systemMandatory ?? raw.system_mandatory ?? false,
    field_read_only: raw.fieldReadOnly ?? raw.field_read_only ?? false,
    pick_list_values: raw.pickListValues ?? raw.pick_list_values,
  };
};

// Pretty-print a group key (e.g. "Account_Name") as a human label using the
// first field's lookupModuleDisplayName when present, otherwise turning
// underscores into spaces.
const prettifyGroupName = (key, list) => {
  const fromField = list?.[0]?.lookupModuleDisplayName;
  if (fromField) return fromField;
  return String(key).replace(/_/g, " ");
};

export const getZohoModuleFieldsViaApi = async (headers, moduleName) => {
  if (!moduleName) return [];
  try {
    const resp = await axios({
      method: "POST",
      url: `${SERVICE_API_URL}/shopify/getcrmfieids`,
      headers: { ...headers, "Content-Type": "application/json" },
      data: {
        entity: moduleName,
        displayName: true,
        apiName: true,
        returnValues: ["fields"],
        fetchAdditionalFieldTypes: ["entity", "lookup", "ownerlookup", "subform"],
      },
    });

    const body = resp?.data;
    const out = [];

    // New shape — { fieldNames: { <groupApiName>: [field, …], … }, … }.
    // The main module's own fields appear under a key matching `moduleName`;
    // additional groups (Owner, Account_Name, Line_Items, …) carry the
    // cross-module fields reachable from that path. We flatten everything
    // into a single list and tag each field with its group so the UI can
    // render an Autocomplete grouped by module.
    const fieldNames = body?.fieldNames;
    if (
      fieldNames &&
      typeof fieldNames === "object" &&
      !Array.isArray(fieldNames)
    ) {
      for (const [groupKey, list] of Object.entries(fieldNames)) {
        if (!Array.isArray(list)) continue;
        const isMain = groupKey === moduleName;
        const groupLabel = prettifyGroupName(groupKey, list);
        for (const raw of list) {
          const norm = normaliseRestZohoField(raw);
          // Qualify cross-module field api_names with the group prefix so the
          // backend can resolve the path on save (e.g. "Account_Name.Industry").
          // Main-module fields keep their bare api_name.
          if (!isMain && norm.api_name) {
            norm.api_name = `${groupKey}.${norm.api_name}`;
          }
          norm._group = groupLabel;
          norm._groupKey = groupKey;
          norm._isMainModule = isMain;
          out.push(norm);
        }
      }
    } else {
      // Legacy / fallback shapes:
      //   { fields: [...] }
      //   { data: { fields: [...] } }
      //   { data: [...] }
      //   [...]
      let fields = [];
      if (Array.isArray(body)) fields = body;
      else if (Array.isArray(body?.fields)) fields = body.fields;
      else if (Array.isArray(body?.data)) fields = body.data;
      else if (Array.isArray(body?.data?.fields)) fields = body.data.fields;
      for (const raw of fields) {
        const norm = normaliseRestZohoField(raw);
        norm._group = moduleName;
        norm._groupKey = moduleName;
        norm._isMainModule = true;
        out.push(norm);
      }
    }

    return out.filter((f) => f.field_read_only !== true);
  } catch (error) {
    console.error("getZohoModuleFieldsViaApi error:", error);
    return [];
  }
};
